import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Permission, Role } from '../types'
import { permissionService } from '../services/permissionService'
import { useAuth } from './AuthContext'

interface PermissionContextType {
  permissions: Permission[]
  roles: Role[]
  loading: boolean
  error: string | null
  
  // Permission checking methods
  hasPermission: (permissionName: string) => boolean
  hasRole: (roleName: string) => boolean
  hasAnyRole: (roleNames: string[]) => boolean
  hasResourcePermission: (resource: string, action: string) => boolean
  
  // Convenience methods
  canCreate: (resource: string) => boolean
  canRead: (resource: string) => boolean
  canUpdate: (resource: string) => boolean
  canDelete: (resource: string) => boolean
  
  // Admin checks
  isAdmin: () => boolean
  isSuperAdmin: () => boolean
  
  // Resource-specific checks
  canManageTransactions: () => boolean
  canManageCategories: () => boolean
  canManageGroups: () => boolean
  canManageSystem: () => boolean
  
  // Refresh permissions
  refreshPermissions: () => Promise<void>
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined)

interface PermissionProviderProps {
  children: ReactNode
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({ children }) => {
  const { user } = useAuth()
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [roles, setRoles] = useState<Role[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadUserPermissions = async () => {
    if (!user) {
      setPermissions([])
      setRoles([])
      return
    }

    try {
      setLoading(true)
      setError(null)
      
      const data = await permissionService.getUserPermissions(user.id)
      setPermissions(data.permissions || [])
      setRoles(data.roles || [])
    } catch (err) {
      console.error('Failed to load user permissions:', err)
      setError('Failed to load permissions')
      setPermissions([])
      setRoles([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUserPermissions()
  }, [user])

  // Permission checking methods
  const hasPermission = (permissionName: string): boolean => {
    return permissionService.hasPermission(permissions, permissionName)
  }

  const hasRole = (roleName: string): boolean => {
    return permissionService.hasRole(roles, roleName)
  }

  const hasAnyRole = (roleNames: string[]): boolean => {
    return permissionService.hasAnyRole(roles, roleNames)
  }

  const hasResourcePermission = (resource: string, action: string): boolean => {
    return permissionService.hasResourcePermission(permissions, resource, action)
  }

  // Convenience methods
  const canCreate = (resource: string): boolean => {
    return permissionService.canCreate(permissions, resource)
  }

  const canRead = (resource: string): boolean => {
    return permissionService.canRead(permissions, resource)
  }

  const canUpdate = (resource: string): boolean => {
    return permissionService.canUpdate(permissions, resource)
  }

  const canDelete = (resource: string): boolean => {
    return permissionService.canDelete(permissions, resource)
  }

  // Admin checks
  const isAdmin = (): boolean => {
    return permissionService.isAdmin(roles)
  }

  const isSuperAdmin = (): boolean => {
    return permissionService.isSuperAdmin(roles)
  }

  // Resource-specific checks
  const canManageTransactions = (): boolean => {
    return permissionService.canManageTransactions(permissions)
  }

  const canManageCategories = (): boolean => {
    return permissionService.canManageCategories(permissions)
  }

  const canManageGroups = (): boolean => {
    return permissionService.canManageGroups(permissions)
  }

  const canManageSystem = (): boolean => {
    return permissionService.canManageSystem(permissions)
  }

  const refreshPermissions = async (): Promise<void> => {
    await loadUserPermissions()
  }

  const value: PermissionContextType = {
    permissions,
    roles,
    loading,
    error,
    hasPermission,
    hasRole,
    hasAnyRole,
    hasResourcePermission,
    canCreate,
    canRead,
    canUpdate,
    canDelete,
    isAdmin,
    isSuperAdmin,
    canManageTransactions,
    canManageCategories,
    canManageGroups,
    canManageSystem,
    refreshPermissions
  }

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  )
}

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext)
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider')
  }
  return context
}